import { RoleName } from "@/api/users-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { useHasRole } from "@/hooks/use-has-role";
import { usePromptById } from "@/queries/prompts/use-prompt-by-id";
import { useUpdatePrompt } from "@/queries/prompts/use-update-prompt";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";

export function UpdatePrompt({ promptId }: { promptId: string }) {
  const [open, setOpen] = useState(false);

  const canUpdate = useHasRole(RoleName.promptEngineer);

  if (!canUpdate) {
    return null;
  }

  return (
    <>
      <UpdateButton onClick={() => setOpen(true)} />
      <UpdateDialog
        open={open}
        promptId={promptId}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function UpdateButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant={"contained"} onClick={onClick}>
      Update description
    </Button>
  );
}

function UpdateDialog({
  open,
  promptId,
  onClose,
}: {
  open: boolean;
  promptId: string;
  onClose: () => void;
}) {
  const { data: prompt } = usePromptById(promptId);

  const { mutate: updatePrompt, isPending, error } = useUpdatePrompt();

  const [description, setDescription] = useState<string>(); // undefined means input not touched
  const [descriptionError, setDescriptionError] = useState<string>();

  if (!prompt) {
    return null;
  }

  const initialDescription = prompt.description ?? "";

  function handleClose() {
    setDescription(undefined);
    setDescriptionError(undefined);
    onClose();
  }

  function handleUpdate() {
    const isDescriptionUnchanged =
      description === undefined || description === initialDescription;
    if (isDescriptionUnchanged) {
      setDescriptionError(
        "New description must be different from the current one",
      );
      return;
    }
    updatePrompt(
      { promptId, data: { description } },
      {
        onSuccess: () => {
          handleClose();
        },
      },
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form>
        <DialogTitle>Update Prompt Description</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={error ? 0 : 0.75}>
            {error && <ApiErrorAlert error={error} />}
            <UpdatePromptForm
              description={description ?? initialDescription}
              descriptionError={descriptionError}
              onDescriptionChange={(value) => {
                setDescription(value);
                setDescriptionError(undefined);
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            loading={isPending}
            variant={"contained"}
          >
            Update
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function UpdatePromptForm({
  description,
  descriptionError,
  onDescriptionChange,
}: {
  description: string;
  descriptionError?: string;
  onDescriptionChange: (description: string) => void;
}) {
  return (
    <TextField
      label="Description"
      error={!!descriptionError}
      helperText={descriptionError}
      multiline={true}
      id="update-prompt-form_description"
      value={description}
      minRows={5}
      onChange={(e) => {
        onDescriptionChange(e.target.value);
      }}
    />
  );
}
